import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  // Still-MUI primitives kept for the Table + Dialogs migrated in follow-up
  // commits. The page chrome (header, tabs, search/filter, bulk-actions
  // toolbar) has moved to shadcn primitives below. Renamed imports (MuiX)
  // are used inside the dialog/table bodies; their callsites swap to shadcn
  // in subsequent commits.
  Box, Card, Typography, Button as MuiButton, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Select as MuiSelect, MenuItem, FormControl, InputLabel,
  IconButton, Stack, Chip, Checkbox as MuiCheckbox, Autocomplete,
  Tooltip as MuiTooltip, FormControlLabel, LinearProgress, List, ListItem,
  ListItemText, ListItemSecondaryAction, Divider, Alert, CircularProgress,
} from '@mui/material';
import {
  Delete as DeleteIcon, AttachFile as AttachFileIcon,
  Download as DownloadIcon, Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon, Mail as MailIcon,
} from '@mui/icons-material';
import {
  Download as DownloadLucide, Plus, Search, X, Trash2,
  ArrowRight as CarryRight, Pencil, CheckCircle2, Send, Mail,
  Repeat, Ban, Sparkles,
} from 'lucide-react';
import {
  Tooltip, TooltipContent, TooltipTrigger,
} from '@/components/ui/tooltip';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import { workItemsApi, categoriesApi, tagsApi, authApi, projectsApi, driversApi, exportApi, rhythmApi } from '../api/services';
import type { WorkItem, WorkItemAttachment, WorkItemType, Priority, WorkItemStatus, WorkItemFlag, Category, Tag, Project, AnnualStrategicDriver } from '../types/models';
import PriorityChip from '../components/PriorityChip';
import StatusChip from '../components/StatusChip';
import HelpPanel from '../components/HelpPanel';
import DriverPill from '../components/DriverPill';
import { WorkItemTypeBadge } from '../components/workItems/WorkItemTypeTreatment';
import { isAllowedEmailLink, openEmailLink } from '../utils/emailLink';
import { suggestWorkItemType } from '../utils/suggestWorkItemType';
import { workItemsCopy } from '../copy/workItems';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

const todayStr = format(new Date(), 'yyyy-MM-dd');

function getCarryForwardDays(item: WorkItem): number | null {
  if (!item.isCarryForward || !item.originalDate) return null;
  return Math.ceil((new Date().getTime() - new Date(item.originalDate).getTime()) / 86400000);
}

const FLAG_OPTIONS: WorkItemFlag[] = [
  'Leadership', 'Dependency', 'Urgent', 'Informational',
  'FollowUp', 'DecisionRequired',
];

const workItemTypes: WorkItemType[] = ['Task', 'Decision', 'Briefing', 'Review', 'FollowUp', 'Draft', 'Reading'];
const priorities: Priority[] = ['Critical', 'High', 'Medium', 'Low'];
const statuses: WorkItemStatus[] = ['Backlog', 'Planned', 'InProgress', 'Done', 'Deferred', 'Cancelled'];
const recurrencePatterns = ['Daily', 'Weekly', 'Biweekly', 'Monthly', 'Quarterly'];
const weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function WorkItemsPage() {
  const location = useLocation();
  const [items, setItems] = useState<WorkItem[]>([]);
  const [loading, setLoading] = useState(true); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState<Partial<WorkItem> | null>(null);
  const [viewItem, setViewItem] = useState<WorkItem | null>(null);
  const [tabValue, setTabValue] = useState(0);

  // Bulk selection
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Search & filter
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('');
  const [filterPriority, setFilterPriority] = useState<string>('');

  // Categories & tags for the create/edit dialog
  const [categories, setCategories] = useState<Category[]>([]);
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [editTagNames, setEditTagNames] = useState<string[]>([]);
  const [editFlags, setEditFlags] = useState<string[]>([]);

  // Delegation state
  const [delegateDialogOpen, setDelegateDialogOpen] = useState(false);
  const [delegateItem, setDelegateItem] = useState<WorkItem | null>(null);
  const [delegateTarget, setDelegateTarget] = useState('');
  const [delegateDate, setDelegateDate] = useState('');
  const [delegateNotes, setDelegateNotes] = useState('');
  const [executives, setExecutives] = useState<{ id: string; displayName: string; email: string }[]>([]);
  const [activeProjects, setActiveProjects] = useState<Project[]>([]);
  const [strategicDrivers, setStrategicDrivers] = useState<AnnualStrategicDriver[]>([]);

  // Attachments state
  const [attachments, setAttachments] = useState<WorkItemAttachment[]>([]);
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Slot suggestions
  const [slotSuggestions, setSlotSuggestions] = useState<Array<{ startTime: string; endTime: string; score: number; reason: string }>>([]);
  const [suggestingSlots, setSuggestingSlots] = useState(false);
  const [schedulingItem, setSchedulingItem] = useState(false);
  const [scheduleSuccess, setScheduleSuccess] = useState<string | null>(null);

  const loadItems = async () => {
    setLoading(true);
    try {
      const data = await workItemsApi.getAll();
      setItems(data);
    } catch (err) {
      console.error('Failed to load work items', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadItems(); }, []);

  // Load categories, tags, executives, projects, and drivers once
  useEffect(() => {
    categoriesApi.getAll().then(setCategories).catch(() => {});
    tagsApi.getAll().then(setAllTags).catch(() => {});
    authApi.getUsers().then(users => {
      setExecutives(users.filter((u: any) => u.role === 'Executive'));
    }).catch(() => {});
    projectsApi.getAll({ status: 'Active' }).then(setActiveProjects).catch(() => {});
    driversApi.getAll().then(setStrategicDrivers).catch(() => {});
  }, []);

  // Open create dialog when navigated with ?action=create
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('action') === 'create' && !dialogOpen) {
      setEditItem({
        title: '', type: 'Task', priority: 'Medium', status: 'Backlog',
        estimatedMinutes: 30, targetDate: format(new Date(), 'yyyy-MM-dd'),
        flags: [],
      });
      setEditTagNames([]);
      setEditFlags([]);
      setAttachments([]);
      setSlotSuggestions([]);
      setDialogOpen(true);
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Attachment helpers
  const loadAttachments = useCallback(async (workItemId: string) => {
    try {
      const data = await workItemsApi.getAttachments(workItemId);
      setAttachments(data);
    } catch {
      setAttachments([]);
    }
  }, []);

  const handleUploadAttachment = async (file: File) => {
    if (!editItem?.id) return;
    if (file.size > 5 * 1024 * 1024) {
      alert('File size exceeds 5 MB limit.');
      return;
    }
    if (attachments.length >= 10) {
      alert('Maximum 10 attachments per work item.');
      return;
    }
    setAttachmentUploading(true);
    try {
      await workItemsApi.uploadAttachment(editItem.id, file);
      await loadAttachments(editItem.id);
    } catch (err) {
      console.error('Failed to upload attachment', err);
      alert('Failed to upload attachment.');
    } finally {
      setAttachmentUploading(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    if (!editItem?.id) return;
    if (!window.confirm('Delete this attachment?')) return;
    try {
      await workItemsApi.deleteAttachment(editItem.id, attachmentId);
      await loadAttachments(editItem.id);
    } catch (err) {
      console.error('Failed to delete attachment', err);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Slot suggestion helpers
  const handleSuggestSlots = async () => {
    if (!editItem?.estimatedMinutes || !editItem?.targetDate) return;
    setSuggestingSlots(true);
    setSlotSuggestions([]);
    try {
      const result = await rhythmApi.suggestSlots(
        editItem.estimatedMinutes,
        editItem.targetDate,
        editItem.priority?.toLowerCase()
      );
      setSlotSuggestions(result.suggestions || []);
    } catch (err) {
      console.error('Failed to suggest slots', err);
    } finally {
      setSuggestingSlots(false);
    }
  };

  const handleScheduleItem = async (workItemId: string, targetDate?: string) => {
    setSchedulingItem(true);
    setScheduleSuccess(null);
    try {
      const date = targetDate || editItem?.targetDate || todayStr;
      const result = await rhythmApi.autoSchedule({ date, workItemIds: [workItemId] });
      if (result.createdBlocks.length > 0) {
        const block = result.createdBlocks[0];
        const start = new Date(block.startTime);
        const end = new Date(block.endTime);
        const timeStr = `${start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
        setScheduleSuccess(`Scheduled for ${timeStr}`);
      } else if (result.conflicts.length > 0) {
        setScheduleSuccess(result.conflicts[0]);
      } else {
        setScheduleSuccess(result.message);
      }
      setSlotSuggestions([]);
      await loadItems();
    } catch (err) {
      console.error('Failed to schedule work item', err);
    } finally {
      setSchedulingItem(false);
    }
  };

  // Dependency helpers
  const getBlockingItems = useCallback((itemId: string): WorkItem[] => {
    return items.filter(i => i.blockedByWorkItemId === itemId);
  }, [items]);

  const handleSave = async () => {
    if (!editItem?.title) return;
    // Block save on an invalid email link so the user sees the inline error
    // before the backend 400. An empty/whitespace link is allowed (clears).
    const rawLink = (editItem.emailLink ?? '').trim();
    if (rawLink.length > 0 && !isAllowedEmailLink(rawLink)) {
      return;
    }
    try {
      const payload = {
        ...editItem,
        emailLink: rawLink.length === 0 ? '' : rawLink,
        flags: editFlags as WorkItemFlag[],
        // Clear recurrence fields if not recurring
        ...(!editItem.isRecurring && {
          recurrencePattern: undefined,
          recurrenceDays: undefined,
          recurrenceEndDate: undefined,
        }),
      };
      if (editItem.id) {
        await workItemsApi.update(editItem.id, payload);
      } else {
        await workItemsApi.create(payload);
      }
      setDialogOpen(false);
      setEditItem(null);
      setEditFlags([]);
      loadItems();
    } catch (err) {
      console.error('Failed to save work item', err);
    }
  };

  const handleOpenDelegate = (item: WorkItem) => {
    setDelegateItem(item);
    setDelegateTarget('');
    setDelegateDate(item.targetDate || '');
    setDelegateNotes('');
    setDelegateDialogOpen(true);
  };

  const handleDelegate = async () => {
    if (!delegateItem || !delegateTarget) return;
    try {
      await workItemsApi.delegate(delegateItem.id, {
        targetExecutiveUserId: delegateTarget,
        targetDate: delegateDate || undefined,
        notes: delegateNotes || undefined,
      });
      setDelegateDialogOpen(false);
      setDelegateItem(null);
      loadItems();
    } catch (err) {
      console.error('Failed to delegate work item', err);
    }
  };

  const handleComplete = async (id: string) => {
    try {
      await workItemsApi.update(id, { status: 'Done' });
      loadItems();
    } catch (err) {
      console.error('Failed to complete', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this work item?')) return;
    try {
      await workItemsApi.delete(id);
      loadItems();
    } catch (err) {
      console.error('Failed to delete', err);
    }
  };

  const handleCarryForward = async (id: string) => {
    const tomorrow = format(new Date(Date.now() + 86400000), 'yyyy-MM-dd');
    try {
      await workItemsApi.carryForward(id, tomorrow);
      loadItems();
    } catch (err) {
      console.error('Failed to carry forward', err);
    }
  };

  // Bulk actions
  const handleBulkCarryForward = async () => {
    const tomorrow = format(new Date(Date.now() + 86400000), 'yyyy-MM-dd');
    try {
      await Promise.all(Array.from(selected).map(id => workItemsApi.carryForward(id, tomorrow)));
      setSelected(new Set());
      loadItems();
    } catch (err) {
      console.error('Bulk carry forward failed', err);
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.size} selected work items?`)) return;
    try {
      await Promise.all(Array.from(selected).map(id => workItemsApi.delete(id)));
      setSelected(new Set());
      loadItems();
    } catch (err) {
      console.error('Bulk delete failed', err);
    }
  };

  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (ids: string[]) => {
    const allSelected = ids.every(id => selected.has(id));
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(ids));
    }
  };

  // Tab filter — Today is now first (index 0)
  const todayItems = items.filter(i => i.targetDate === todayStr);
  const tabFiltered = tabValue === 0
    ? todayItems
    : tabValue === 1
      ? items.filter(i => !['Done', 'Cancelled'].includes(i.status))
      : tabValue === 2
        ? items.filter(i => i.status === 'Done')
        : items;

  // Search & filter
  const filteredItems = useMemo(() => {
    let result = tabFiltered;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(i =>
        i.title.toLowerCase().includes(q) || (i.description || '').toLowerCase().includes(q)
      );
    }
    if (filterType) result = result.filter(i => i.type === filterType);
    if (filterPriority) result = result.filter(i => i.priority === filterPriority);
    return result;
  }, [tabFiltered, searchQuery, filterType, filterPriority]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleExportCsv = async () => {
    try {
      const response = await exportApi.downloadWorkItemsCsv();
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `work-items-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert('Failed to export work items.');
    }
  };

  // Sentinel "all" value for the shadcn Select. Radix Select can't have an
  // empty-string value as a SelectItem, so we round-trip "all" ↔ '' at the
  // boundary instead of changing the surrounding filter logic.
  const ALL = 'all';
  const tabLabels: Array<{ value: string; label: string }> = [
    { value: '0', label: `Today (${items.filter(i => i.targetDate === todayStr).length})` },
    { value: '1', label: `Active (${items.filter(i => !['Done', 'Cancelled'].includes(i.status)).length})` },
    { value: '2', label: `Completed (${items.filter(i => i.status === 'Done').length})` },
    { value: '3', label: `All (${items.length})` },
  ];

  return (
    <div>
      <div className="mb-6 flex items-center justify-between gap-2">
        <h1 className="text-2xl font-semibold tracking-tight">Work Items</h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleExportCsv} className="gap-1.5">
            <DownloadLucide className="h-4 w-4" />
            Export CSV
          </Button>
          <Button
            className="gap-1.5"
            onClick={() => {
              setEditItem({
                title: '', type: 'Task', priority: 'Medium', status: 'Backlog',
                estimatedMinutes: 30, targetDate: format(new Date(), 'yyyy-MM-dd'),
                flags: [],
              });
              setEditTagNames([]);
              setEditFlags([]);
              setAttachments([]);
              setSlotSuggestions([]);
              setDialogOpen(true);
            }}
          >
            <Plus className="h-4 w-4" />
            Add Work Item
          </Button>
        </div>
      </div>

      <HelpPanel title="About Work Items" storageKey="workitems">
        Work items are tasks, decisions, briefings, and reviews. Use flags to mark urgency. Drag to reorder priority.
      </HelpPanel>

      <Tabs
        value={String(tabValue)}
        onValueChange={(v) => { setTabValue(Number(v)); setSelected(new Set()); }}
        className="mb-4"
      >
        <TabsList>
          {tabLabels.map(t => (
            <TabsTrigger key={t.value} value={t.value}>{t.label}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Search & filter bar */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[240px]">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search title or description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8"
          />
        </div>
        <Select
          value={filterType || ALL}
          onValueChange={(v) => setFilterType(v === ALL ? '' : v)}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All types</SelectItem>
            {workItemTypes.map(t => (
              <SelectItem key={t} value={t}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={filterPriority || ALL}
          onValueChange={(v) => setFilterPriority(v === ALL ? '' : v)}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All priorities</SelectItem>
            {priorities.map(p => (
              <SelectItem key={p} value={p}>{p}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {(searchQuery || filterType || filterPriority) && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => { setSearchQuery(''); setFilterType(''); setFilterPriority(''); }}
            className="gap-1.5"
          >
            <X className="h-3.5 w-3.5" />
            Clear
          </Button>
        )}
      </div>

      {/* Bulk actions toolbar — only when something is selected. */}
      {selected.size > 0 && (
        <div className="mb-2 flex h-12 items-center gap-3 rounded-2xl bg-primary/10 px-4">
          <p className="flex-1 text-sm font-semibold">{selected.size} selected</p>
          <Button size="sm" variant="outline" onClick={handleBulkCarryForward} className="gap-1.5">
            <CarryRight className="h-3.5 w-3.5" />
            Carry Forward All
          </Button>
          <Button size="sm" variant="destructive" onClick={handleBulkDelete} className="gap-1.5">
            <Trash2 className="h-3.5 w-3.5" />
            Delete Selected
          </Button>
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-border bg-card/40 backdrop-blur">
        <table className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr className="border-b border-border text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
              <th className="w-10 px-2 py-2">
                <Checkbox
                  checked={
                    filteredItems.length > 0 && selected.size === filteredItems.length
                      ? true
                      : selected.size > 0 && selected.size < filteredItems.length
                        ? 'indeterminate'
                        : false
                  }
                  onCheckedChange={() => toggleSelectAll(filteredItems.map(i => i.id))}
                  aria-label="Select all"
                />
              </th>
              <th className="px-3 py-2">Title</th>
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2">Priority</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Duration</th>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">Flags</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.map((item) => {
              const isDone = item.status === 'Done';
              const isCancelled = item.status === 'Cancelled';
              const rowSelected = selected.has(item.id);
              const blockingCount = getBlockingItems(item.id).length;
              const blockedByTitle = item.blockedByWorkItemId
                ? items.find(i => i.id === item.blockedByWorkItemId)?.title || 'Unknown'
                : null;
              return (
                <tr
                  key={item.id}
                  onClick={() => setViewItem(item)}
                  className={cn(
                    'cursor-pointer border-b border-border last:border-b-0 transition-colors hover:bg-muted/30',
                    rowSelected && 'bg-primary/5',
                    (isDone || isCancelled) && 'opacity-50',
                  )}
                >
                  <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={rowSelected}
                      onCheckedChange={() => toggleSelect(item.id)}
                      aria-label={`Select ${item.title}`}
                    />
                  </td>
                  <td className="min-w-0 px-3 py-2">
                    <div className="flex min-w-0 items-center gap-2">
                      <p className={cn(
                        'truncate text-sm font-medium',
                        (isDone || isCancelled) && 'line-through',
                      )}>
                        {isDone && '✓ '}{item.title}
                      </p>
                      <DriverPill driver={item.strategicDriver} size="compact" />
                    </div>
                    {item.description && (
                      <p className="mt-0.5 truncate text-xs text-muted-foreground">
                        {item.description.substring(0, 80)}{item.description.length > 80 ? '...' : ''}
                      </p>
                    )}
                  </td>
                  <td className="px-3 py-2"><WorkItemTypeBadge type={item.type} variant="full" /></td>
                  <td className="px-3 py-2"><PriorityChip priority={item.priority} /></td>
                  <td className="px-3 py-2"><StatusChip status={item.status} /></td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5 text-xs">
                      <span>{item.estimatedMinutes}m</span>
                      {item.aiEstimatedMinutes != null && item.aiEstimatedMinutes !== item.estimatedMinutes && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-flex items-center gap-0.5 rounded-full border border-blue-500/60 px-1.5 py-0.5 text-[10px] font-medium text-blue-600 dark:text-blue-400">
                              <Sparkles className="h-3 w-3" />
                              {item.aiEstimatedMinutes}m
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>AI estimated: {item.aiEstimatedMinutes}m</TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs">{item.targetDate}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap items-center gap-1">
                      {item.isCarryForward && (() => {
                        const days = getCarryForwardDays(item);
                        const overdue = days != null && days > 3;
                        return (
                          <span className={cn(
                            'inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium',
                            overdue
                              ? 'border-rose-500/60 text-rose-600 dark:text-rose-400'
                              : 'border-amber-500/60 text-amber-700 dark:text-amber-400',
                          )}>
                            {days != null ? `Carried ${days} day${days !== 1 ? 's' : ''}` : 'Carried'}
                          </span>
                        );
                      })()}
                      {item.isRecurring && !item.recurrenceParentId && (
                        <span className="inline-flex items-center gap-0.5 rounded-full border border-blue-500/60 px-2 py-0.5 text-[10px] font-medium text-blue-600 dark:text-blue-400">
                          <Repeat className="h-3 w-3" />
                          {item.recurrencePattern || 'Recurring'}
                        </span>
                      )}
                      {item.recurrenceParentId && (
                        <span className="inline-flex items-center gap-0.5 rounded-full border border-border px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                          <Repeat className="h-3 w-3" />
                          Instance
                        </span>
                      )}
                      {item.blockedByWorkItemId && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-flex items-center gap-0.5 rounded-full border border-rose-500/60 px-2 py-0.5 text-[10px] font-medium text-rose-600 dark:text-rose-400">
                              <Ban className="h-3 w-3" />
                              Blocked
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>Blocked by: {blockedByTitle}</TooltipContent>
                        </Tooltip>
                      )}
                      {blockingCount > 0 && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-flex items-center gap-0.5 rounded-full border border-amber-500/60 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-400">
                              <Ban className="h-3 w-3" />
                              Blocking {blockingCount}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            Blocking: {getBlockingItems(item.id).map(i => i.title).join(', ')}
                          </TooltipContent>
                        </Tooltip>
                      )}
                      {item.flags?.filter(f => f !== 'CarryForward').map(f => (
                        <span key={f} className="inline-flex items-center rounded-full border border-border px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                          {f}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-0.5">
                      {isAllowedEmailLink(item.emailLink) && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button size="icon-sm" variant="ghost" onClick={() => openEmailLink(item.emailLink)}>
                              <Mail className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>{workItemsCopy.openEmail}</TooltipContent>
                        </Tooltip>
                      )}
                      {!isDone && !isCancelled && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size="icon-sm"
                              variant="ghost"
                              onClick={() => handleComplete(item.id)}
                              className="text-emerald-600 hover:text-emerald-700 dark:text-emerald-400"
                            >
                              <CheckCircle2 className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Mark complete</TooltipContent>
                        </Tooltip>
                      )}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="icon-sm"
                            variant="ghost"
                            onClick={() => {
                              setEditItem(item);
                              setEditTagNames(item.workItemTags?.map(wt => wt.tag?.name || '').filter(Boolean) as string[] || []);
                              setEditFlags(item.flags?.filter(f => !['CarryForward', 'ImageDerived'].includes(f)) || []);
                              loadAttachments(item.id);
                              setDialogOpen(true);
                            }}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Edit</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button size="icon-sm" variant="ghost" onClick={() => handleCarryForward(item.id)}>
                            <CarryRight className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Carry forward to tomorrow</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button size="icon-sm" variant="ghost" onClick={() => handleOpenDelegate(item)}>
                            <Send className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Delegate</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button size="icon-sm" variant="ghost" onClick={() => handleDelete(item.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Delete</TooltipContent>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filteredItems.length === 0 && (
              <tr>
                <td colSpan={9} className="py-10 text-center text-sm text-muted-foreground">
                  No work items
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editItem?.id ? 'Edit Work Item' : 'New Work Item'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Title" fullWidth value={editItem?.title || ''} onChange={e => setEditItem(prev => ({ ...prev, title: e.target.value }))} />
            <TextField label="Description" fullWidth multiline rows={3} value={editItem?.description || ''} onChange={e => setEditItem(prev => ({ ...prev, description: e.target.value }))} />
            {/* Type suggestion — only visible when the current type is still
                the default 'Task' AND the title/description matches a non-Task
                pattern. Click the chip to apply. Recommendation §5.5
                "auto-suggest type on creation". */}
            {(() => {
              const currentType = editItem?.type || 'Task';
              if (currentType !== 'Task') return null;
              const suggestion = suggestWorkItemType(editItem?.title || '', editItem?.description);
              if (!suggestion) return null;
              return (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: -1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Looks like a <strong>{suggestion.type}</strong> — {suggestion.reason}.
                  </Typography>
                  <MuiButton
                    size="small"
                    variant="outlined"
                    sx={{ py: 0.25, fontSize: '0.7rem' }}
                    onClick={() => setEditItem(prev => ({ ...prev, type: suggestion.type as WorkItemType }))}
                  >
                    Use {suggestion.type}
                  </MuiButton>
                </Stack>
              );
            })()}
            <Stack direction="row" spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <MuiSelect value={editItem?.type || 'Task'} label="Type" onChange={e => setEditItem(prev => ({ ...prev, type: e.target.value as WorkItemType }))}>
                  {workItemTypes.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </MuiSelect>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <MuiSelect value={editItem?.priority || 'Medium'} label="Priority" onChange={e => setEditItem(prev => ({ ...prev, priority: e.target.value as Priority }))}>
                  {priorities.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                </MuiSelect>
              </FormControl>
            </Stack>
            <Stack direction="row" spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <MuiSelect value={editItem?.status || 'Backlog'} label="Status" onChange={e => setEditItem(prev => ({ ...prev, status: e.target.value as WorkItemStatus }))}>
                  {statuses.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </MuiSelect>
              </FormControl>
              <TextField label="Duration (min)" type="number" fullWidth value={editItem?.estimatedMinutes || 30} onChange={e => setEditItem(prev => ({ ...prev, estimatedMinutes: parseInt(e.target.value) || 30 }))} />
            </Stack>
            <TextField label="Target Date" type="date" fullWidth InputLabelProps={{ shrink: true }} value={editItem?.targetDate || ''} onChange={e => { setEditItem(prev => ({ ...prev, targetDate: e.target.value })); setSlotSuggestions([]); }} />
            {editItem?.estimatedMinutes && editItem?.targetDate && (
              <Box>
                <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                  <MuiButton
                    size="small"
                    variant="outlined"
                    startIcon={<ScheduleIcon />}
                    onClick={handleSuggestSlots}
                    disabled={suggestingSlots || schedulingItem}
                  >
                    {suggestingSlots ? 'Finding slots...' : 'Suggest Time'}
                  </MuiButton>
                  {editItem?.id && (
                    <MuiButton
                      size="small"
                      variant="contained"
                      startIcon={schedulingItem ? <CircularProgress size={14} color="inherit" /> : <ScheduleIcon />}
                      onClick={() => handleScheduleItem(editItem!.id!)}
                      disabled={schedulingItem || suggestingSlots}
                    >
                      {schedulingItem ? 'Scheduling...' : 'Schedule'}
                    </MuiButton>
                  )}
                </Stack>
                {scheduleSuccess && (
                  <Alert severity="success" sx={{ mb: 1 }} onClose={() => setScheduleSuccess(null)}>
                    {scheduleSuccess}
                  </Alert>
                )}
                {suggestingSlots && <LinearProgress sx={{ mb: 1 }} />}
                {slotSuggestions.length > 0 && (
                  <Stack spacing={0.5}>
                    {slotSuggestions.map((slot, idx) => {
                      const start = new Date(slot.startTime);
                      const end = new Date(slot.endTime);
                      const timeStr = `${start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
                      const scorePercent = Math.round(slot.score * 100);
                      return (
                        <Card
                          key={idx}
                          variant="outlined"
                          sx={{
                            p: 1, cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' },
                            borderColor: idx === 0 ? 'primary.main' : 'divider',
                          }}
                          onClick={() => {
                            const dateStr = format(start, 'yyyy-MM-dd');
                            setEditItem(prev => ({ ...prev, targetDate: dateStr }));
                            setSlotSuggestions([]);
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Box>
                              <Typography variant="body2" fontWeight={600}>{timeStr}</Typography>
                              <Typography variant="caption" color="text.secondary">{slot.reason}</Typography>
                            </Box>
                            <Chip
                              label={`${scorePercent}%`}
                              size="small"
                              color={scorePercent >= 80 ? 'success' : scorePercent >= 60 ? 'warning' : 'default'}
                              sx={{ fontWeight: 600 }}
                            />
                          </Stack>
                        </Card>
                      );
                    })}
                    <Typography variant="caption" color="text.secondary">
                      Click a slot to set as target date
                    </Typography>
                  </Stack>
                )}
              </Box>
            )}
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <MuiSelect
                value={editItem?.categoryId || ''}
                label="Category"
                onChange={e => {
                  const cat = categories.find(c => c.id === e.target.value);
                  setEditItem(prev => ({ ...prev, categoryId: e.target.value || null, category: cat?.name || '' }));
                }}
              >
                <MenuItem value="">None</MenuItem>
                {categories.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </MuiSelect>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Project</InputLabel>
              <MuiSelect
                value={editItem?.projectId || ''}
                label="Project"
                onChange={e => {
                  const projectId = e.target.value || undefined;
                  const project = activeProjects.find(p => p.id === projectId);
                  setEditItem(prev => {
                    const next = { ...prev, projectId: projectId || undefined };
                    // Auto-set strategic driver if project has one and user hasn't set one yet
                    if (project?.strategicDriverId && !prev?.strategicDriverId) {
                      next.strategicDriverId = project.strategicDriverId;
                    }
                    return next;
                  });
                }}
              >
                <MenuItem value="">None</MenuItem>
                {activeProjects.map(p => <MenuItem key={p.id} value={p.id}>{p.title}</MenuItem>)}
              </MuiSelect>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Strategic Driver</InputLabel>
              <MuiSelect
                value={editItem?.strategicDriverId || ''}
                label="Strategic Driver"
                onChange={e => setEditItem(prev => ({ ...prev, strategicDriverId: e.target.value || undefined }))}
              >
                <MenuItem value="">None</MenuItem>
                {strategicDrivers.map(d => <MenuItem key={d.id} value={d.id}>{d.title}</MenuItem>)}
              </MuiSelect>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Assign To</InputLabel>
              <MuiSelect
                value={editItem?.assignedToExecutiveId || ''}
                label="Assign To"
                onChange={e => setEditItem(prev => ({ ...prev, assignedToExecutiveId: e.target.value || null }))}
              >
                <MenuItem value="">Unassigned</MenuItem>
                {executives.map(exec => (
                  <MenuItem key={exec.id} value={exec.id}>{exec.displayName} ({exec.email})</MenuItem>
                ))}
              </MuiSelect>
            </FormControl>
            <Autocomplete
              multiple
              options={allTags.map(t => t.name)}
              value={editTagNames}
              onChange={(_, newVal) => setEditTagNames(newVal)}
              freeSolo
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip size="small" label={option} {...getTagProps({ index })} key={option} />
                ))
              }
              renderInput={(params) => <TextField {...params} label="Tags" placeholder="Add tags..." />}
            />
            <Autocomplete
              multiple
              options={FLAG_OPTIONS}
              value={editFlags}
              onChange={(_, v) => setEditFlags(v)}
              renderTags={(value, getTagProps) =>
                value.map((flag, i) => (
                  <Chip label={flag} size="small" {...getTagProps({ index: i })} key={flag} />
                ))
              }
              renderInput={(params) => <TextField {...params} label="Flags" placeholder="Add flags" />}
            />
            <TextField label="Notes" fullWidth multiline rows={2} value={editItem?.notes || ''} onChange={e => setEditItem(prev => ({ ...prev, notes: e.target.value }))} />

            {/* Email link: paste a mailto: URI, an Outlook desktop deep link,
                or an Outlook-web URL. Backend + helper both allow-list the
                three shapes; inline validation warns before the user saves. */}
            {(() => {
              const link = editItem?.emailLink ?? '';
              const trimmed = link.trim();
              const invalid = trimmed.length > 0 && !isAllowedEmailLink(trimmed);
              return (
                <TextField
                  label={workItemsCopy.emailLinkLabel}
                  fullWidth
                  value={link}
                  onChange={e => setEditItem(prev => ({ ...prev, emailLink: e.target.value }))}
                  placeholder="https://outlook.office.com/mail/... or mailto:..."
                  error={invalid}
                  helperText={invalid ? workItemsCopy.emailLinkInvalid : workItemsCopy.emailLinkHelper}
                  InputProps={{
                    endAdornment: trimmed && !invalid ? (
                      <Stack direction="row" spacing={0.5}>
                        <MuiTooltip title={workItemsCopy.openEmail}>
                          <IconButton size="small" onClick={() => openEmailLink(trimmed)}>
                            <MailIcon fontSize="small" />
                          </IconButton>
                        </MuiTooltip>
                        <MuiTooltip title={workItemsCopy.clearEmailLink}>
                          <IconButton size="small" onClick={() => setEditItem(prev => ({ ...prev, emailLink: '' }))}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </MuiTooltip>
                      </Stack>
                    ) : null,
                  }}
                />
              );
            })()}

            {/* Recurrence */}
            <FormControlLabel
              control={
                <MuiCheckbox
                  checked={editItem?.isRecurring || false}
                  onChange={e => setEditItem(prev => ({
                    ...prev,
                    isRecurring: e.target.checked,
                    ...(!e.target.checked && { recurrencePattern: undefined, recurrenceDays: undefined, recurrenceEndDate: undefined }),
                  }))}
                />
              }
              label="Recurring"
            />
            {editItem?.isRecurring && (
              <Stack spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>Recurrence Pattern</InputLabel>
                  <MuiSelect
                    value={editItem?.recurrencePattern || ''}
                    label="Recurrence Pattern"
                    onChange={e => setEditItem(prev => ({ ...prev, recurrencePattern: e.target.value }))}
                  >
                    {recurrencePatterns.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                  </MuiSelect>
                </FormControl>
                {(editItem?.recurrencePattern === 'Weekly' || editItem?.recurrencePattern === 'Biweekly') && (
                  <Autocomplete
                    multiple
                    options={weekDays}
                    value={editItem?.recurrenceDays ? editItem.recurrenceDays.split(',').map(d => d.trim()).filter(Boolean) : []}
                    onChange={(_, newVal) => setEditItem(prev => ({ ...prev, recurrenceDays: newVal.join(',') }))}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip size="small" label={option} {...getTagProps({ index })} key={option} />
                      ))
                    }
                    renderInput={(params) => <TextField {...params} label="Days" placeholder="Select days..." />}
                  />
                )}
                <TextField
                  label="End Date (optional)"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={editItem?.recurrenceEndDate || ''}
                  onChange={e => setEditItem(prev => ({ ...prev, recurrenceEndDate: e.target.value || undefined }))}
                />
              </Stack>
            )}

            {/* Dependencies */}
            <Divider sx={{ my: 1 }} />
            <FormControl fullWidth>
              <InputLabel>Blocked By</InputLabel>
              <MuiSelect
                value={editItem?.blockedByWorkItemId || ''}
                label="Blocked By"
                onChange={async e => {
                  const newValue = e.target.value || undefined;
                  setEditItem(prev => ({ ...prev, blockedByWorkItemId: newValue }));
                  if (editItem?.id) {
                    try {
                      await workItemsApi.setBlockedBy(editItem.id, newValue || null);
                      loadItems();
                    } catch (err) {
                      console.error('Failed to set dependency', err);
                    }
                  }
                }}
              >
                <MenuItem value="">None</MenuItem>
                {items
                  .filter(i => i.id !== editItem?.id && !['Done', 'Cancelled'].includes(i.status))
                  .map(i => (
                    <MenuItem key={i.id} value={i.id}>{i.title}</MenuItem>
                  ))}
              </MuiSelect>
            </FormControl>

            {/* Attachments (only for existing items) */}
            {editItem?.id && (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AttachFileIcon fontSize="small" /> Attachments ({attachments.length}/10)
                </Typography>
                {attachments.length > 0 && (
                  <List dense disablePadding>
                    {attachments.map(att => (
                      <ListItem key={att.id} sx={{ pl: 0, pr: 8 }}>
                        <ListItemText
                          primary={att.fileName}
                          secondary={`${formatFileSize(att.fileSizeBytes)} - ${format(new Date(att.createdAt), 'MMM d, yyyy')}`}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                        <ListItemSecondaryAction>
                          <MuiTooltip title="Download">
                            <IconButton
                              size="small"
                              component="a"
                              href={workItemsApi.downloadAttachmentUrl(editItem.id!, att.id)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </MuiTooltip>
                          <IconButton size="small" onClick={() => handleDeleteAttachment(att.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                )}
                {attachmentUploading && <LinearProgress sx={{ my: 1 }} />}
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) handleUploadAttachment(file);
                    e.target.value = '';
                  }}
                />
                <MuiButton
                  size="small"
                  variant="outlined"
                  startIcon={<AttachFileIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={attachmentUploading || attachments.length >= 10}
                >
                  Add Attachment (max 5 MB)
                </MuiButton>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setDialogOpen(false)}>Cancel</MuiButton>
          <MuiButton variant="contained" onClick={handleSave}>Save</MuiButton>
        </DialogActions>
      </Dialog>

      {/* View Detail Dialog */}
      <Dialog open={!!viewItem} onClose={() => setViewItem(null)} maxWidth="sm" fullWidth>
        {viewItem && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="h6" component="span" sx={{ flex: 1 }}>{viewItem.title}</Typography>
                <Chip label={viewItem.type} size="small" variant="outlined" />
                <Chip label={viewItem.priority} size="small" color={viewItem.priority === 'Critical' ? 'error' : viewItem.priority === 'High' ? 'warning' : 'default'} />
                <Chip label={viewItem.status} size="small" color={viewItem.status === 'Done' ? 'success' : viewItem.status === 'InProgress' ? 'info' : 'default'} />
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2}>
                {viewItem.status === 'Done' && (
                  <Alert severity="success" icon={false} sx={{ py: 0.5 }}>
                    <Typography variant="body2" fontWeight={600}>Completed</Typography>
                  </Alert>
                )}
                {viewItem.status === 'Cancelled' && (
                  <Alert severity="warning" icon={false} sx={{ py: 0.5 }}>
                    <Typography variant="body2" fontWeight={600}>Cancelled</Typography>
                  </Alert>
                )}
                {viewItem.description && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Description</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{viewItem.description}</Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Estimated</Typography>
                    <Typography variant="body2">{viewItem.estimatedMinutes}m</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Target Date</Typography>
                    <Typography variant="body2">{viewItem.targetDate || 'No target date'}</Typography>
                  </Box>
                  {viewItem.category && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>Category</Typography>
                      <Typography variant="body2">{viewItem.category}</Typography>
                    </Box>
                  )}
                </Box>
                {viewItem.notes && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Notes</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{viewItem.notes}</Typography>
                  </Box>
                )}
                {isAllowedEmailLink(viewItem.emailLink) && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Source email</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <MuiButton
                        size="small"
                        variant="outlined"
                        startIcon={<MailIcon fontSize="small" />}
                        onClick={() => openEmailLink(viewItem.emailLink)}
                      >
                        {workItemsCopy.openEmail}
                      </MuiButton>
                    </Box>
                  </Box>
                )}
                {viewItem.flags && viewItem.flags.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Flags</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                      {viewItem.flags.map(f => <Chip key={f} label={f} size="small" variant="outlined" />)}
                    </Box>
                  </Box>
                )}
                {viewItem.isCarryForward && (
                  <Alert severity="info" sx={{ py: 0 }}>
                    Carried forward{viewItem.originalDate ? ` from ${viewItem.originalDate}` : ''}
                  </Alert>
                )}
                {viewItem.isRecurring && (
                  <Alert severity="info" sx={{ py: 0 }}>
                    Recurring: {viewItem.recurrencePattern}{viewItem.recurrenceDays ? ` (${viewItem.recurrenceDays})` : ''}
                  </Alert>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <MuiButton onClick={() => setViewItem(null)}>Close</MuiButton>
              {viewItem.status !== 'Done' && viewItem.status !== 'Cancelled' && (
                <MuiButton variant="contained" color="success" startIcon={<CheckCircleIcon />} onClick={async () => {
                  await handleComplete(viewItem.id);
                  setViewItem(null);
                }}>Mark Complete</MuiButton>
              )}
              <MuiButton variant="outlined" onClick={() => {
                setEditItem(viewItem);
                setEditFlags(viewItem.flags?.filter(f => !['CarryForward', 'ImageDerived'].includes(f)) || []);
                loadAttachments(viewItem.id);
                setDialogOpen(true);
                setViewItem(null);
              }}>Edit</MuiButton>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Delegate Dialog */}
      <Dialog open={delegateDialogOpen} onClose={() => setDelegateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Delegate Work Item</DialogTitle>
        <DialogContent>
          {delegateItem && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Delegating: <strong>{delegateItem.title}</strong>
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Target Executive</InputLabel>
                <MuiSelect
                  value={delegateTarget}
                  label="Target Executive"
                  onChange={e => setDelegateTarget(e.target.value)}
                >
                  {executives.map(exec => (
                    <MenuItem key={exec.id} value={exec.id}>{exec.displayName} ({exec.email})</MenuItem>
                  ))}
                  {executives.length === 0 && (
                    <MenuItem disabled>No executives available</MenuItem>
                  )}
                </MuiSelect>
              </FormControl>
              <TextField
                label="New Target Date (optional)"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={delegateDate}
                onChange={e => setDelegateDate(e.target.value)}
              />
              <TextField
                label="Notes (optional)"
                fullWidth
                multiline
                rows={2}
                value={delegateNotes}
                onChange={e => setDelegateNotes(e.target.value)}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setDelegateDialogOpen(false)}>Cancel</MuiButton>
          <MuiButton variant="contained" onClick={handleDelegate} disabled={!delegateTarget}>
            Delegate
          </MuiButton>
        </DialogActions>
      </Dialog>
    </div>
  );
}
